import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pushApi } from '../api/push.api'

export const PUSH_KEY = ['push'] as const

/**
 * Le navigateur exige la clé VAPID sous forme d'octets, pas de base64url. Le
 * tampon est alloué explicitement : `PushManager` n'accepte qu'un ArrayBuffer.
 */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))

  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index)
  }

  return bytes
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window
}

export function usePushKey() {
  return useQuery({
    queryKey: PUSH_KEY,
    queryFn: () => pushApi.fetchKey().then((r) => r.data.data),
    staleTime: Infinity,
  })
}

/** Abonnement courant du navigateur, ou null s'il n'y en a pas. */
export function useCurrentPushSubscription() {
  return useQuery({
    queryKey: [...PUSH_KEY, 'current'],
    queryFn: async () => {
      if (!pushSupported()) return null

      const registration = await navigator.serviceWorker.ready

      return registration.pushManager.getSubscription()
    },
    staleTime: 30_000,
  })
}

export function useSubscribeToPush(publicKey: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!pushSupported() || publicKey === null) {
        throw new Error('unsupported')
      }

      // L'autorisation se demande au moment du geste : un refus n'est pas une
      // erreur du système, seulement une réponse.
      const permission = await Notification.requestPermission()

      if (permission !== 'granted') {
        throw new Error('denied')
      }

      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })

      const json = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> }

      await pushApi.subscribe({
        endpoint: json.endpoint ?? subscription.endpoint,
        keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
      })

      return subscription
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PUSH_KEY }),
  })
}

export function useUnsubscribeFromPush() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription === null) return

      await pushApi.unsubscribe(subscription.endpoint)
      await subscription.unsubscribe()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PUSH_KEY }),
  })
}
